/**
 * Multi-org access — org sites + subdomain redirect (ENG-359).
 *
 * Mirrors the Gondor mithril helper: an `OrgSite` is a subdomain-addressable
 * presence of an organization on one product (from `GET /auth/organizations`).
 * Switching org navigates to the target host, rebuilt from the site's subdomain
 * + the product's base domain for the current environment.
 */

export type Product = 'HARBOR' | 'MERIDIAN' | 'PLATFORM';

export interface OrgSite {
   org_id: string;
   name: string;
   product: Product;
   subdomain: string;
}

/** Human-facing product names (Brand Bible v2.2 approved names). */
export const PRODUCT_LABELS: Record<Product, string> = {
   HARBOR: 'Harbor',
   MERIDIAN: 'Meridian',
   PLATFORM: 'Fathom',
};

/** Phosphor icon per product, for the workspace switcher rows. */
export const PRODUCT_ICONS: Record<Product, string> = {
   HARBOR: 'ph:clipboard-text', // permitting
   MERIDIAN: 'ph:scales', // PRC filings
   PLATFORM: 'ph:file-text', // document search / platform
};

/** This app (Fathom) is the PLATFORM product surface. */
export const CURRENT_PRODUCT: Product = 'PLATFORM';

export type DeployEnv = 'dev' | 'preview' | 'staging' | 'prod';

const ZONES: Record<Product, { prod: string; staging: string }> = {
   HARBOR: { prod: 'apalumaharbor.com', staging: 'staging.apalumaharbor.com' },
   MERIDIAN: { prod: 'apalumameridian.com', staging: 'staging.apalumameridian.com' },
   PLATFORM: { prod: 'apaluma.com', staging: 'staging.apaluma.com' },
};

const DEV_EDGE: Partial<Record<Product, string>> = {
   HARBOR: 'permits',
   MERIDIAN: 'meridian',
};

/** The set of recognized dev edge labels, derived from `DEV_EDGE`. */
export const DEV_EDGE_LABELS = new Set<string>(Object.values(DEV_EDGE));

export function detectEnv(hostname: string): DeployEnv {
   if (hostname === 'localhost' || hostname.endsWith('.localhost'))
      return 'dev';
   if (hostname.includes('.preview.'))
      return 'preview';
   if (hostname.startsWith('staging.') || hostname.includes('.staging.'))
      return 'staging';
   return 'prod';
}

function swapLeadingLabel(hostname: string, label: string): string {
   return [label, ...hostname.split('.').slice(1)].join('.');
}

function devTicket(hostname: string): string | null {
   const parts = hostname.split('.');
   if (
      parts.length >= 4
      && parts[parts.length - 1] === 'localhost'
      && DEV_EDGE_LABELS.has(parts[2] ?? '')
   ) {
      return parts[1];
   }
   if (
      parts.length === 2
      && parts[parts.length - 1] === 'localhost'
   ) {
      return parts[0];
   }
   return null;
}

export function buildOrgSiteUrl(
   site: Pick<OrgSite, 'product' | 'subdomain'>,
   location: { protocol: string; hostname: string; port: string },
): string {
   const { protocol, hostname, port } = location;
   const env = detectEnv(hostname);
   const portSuffix = port ? `:${port}` : '';

   if (env === 'dev') {
      const ticket = devTicket(hostname);
      if (ticket) {
         // Platform (fathom) runs as the bare, single-tenant ticket host in dev —
         // no client subdomain, no product edge (e.g. eng-359.localhost).
         if (site.product === 'PLATFORM')
            return `${protocol}//${ticket}.localhost${portSuffix}`;
         const edge = DEV_EDGE[site.product];
         if (edge)
            return `${protocol}//${site.subdomain}.${ticket}.${edge}.localhost${portSuffix}`;
      }
      return `${protocol}//${swapLeadingLabel(hostname, site.subdomain)}${portSuffix}`;
   }

   const table = ZONES[site.product];
   let domain: string;
   if (env === 'staging') {
      domain = table.staging;
   }
   else if (env === 'prod') {
      domain = table.prod;
   }
   else {
      domain = hostname.split('.').slice(1).join('.');
   }
   return `${protocol}//${site.subdomain}.${domain}${portSuffix}`;
}

/**
 * Collapse the site list to one entry per org for the picker: prefer the site on
 * `currentProduct` — the product of the app the picker is running in — and only
 * when an org has no site on the current product, fall back to a single
 * other-product site (deterministic: alphabetically-first product). An org never
 * appears more than once (ENG-359).
 */
export function preferredSitesForProduct(sites: OrgSite[], currentProduct: Product): OrgSite[] {
   const byOrg = new Map<string, OrgSite[]>();
   for (const site of sites) {
      const list = byOrg.get(site.org_id) ?? [];
      list.push(site);
      byOrg.set(site.org_id, list);
   }
   const result: OrgSite[] = [];
   for (const orgSites of byOrg.values()) {
      const onCurrent = orgSites.find(s => s.product === currentProduct);
      const fallback = [...orgSites].sort((a, b) => a.product.localeCompare(b.product))[0];
      result.push(onCurrent ?? fallback);
   }
   return result;
}
